import React from "react";
import { useTranslation } from "react-i18next";

type InlineErrorWithContactProps = {
  show: boolean;
  onContact: (e: React.MouseEvent) => void;
  messageKey?: string;
  ariaLabel?: string;
};

/**
 * Ensures the error message ends with punctuation.
 * Checks for common punctuation marks including language-specific ones.
 * Adds a period if no punctuation is found.
 */
function ensurePunctuation(message: string): string {
  if (!message) return message;

  // Trim trailing whitespace to check the last character
  const trimmed = message.trimEnd();
  if (!trimmed) return message;

  // Check for common punctuation marks
  // Includes: period (.), exclamation (!), question mark (?), Bengali/Devanagari period (।)
  const punctuationRegex = /[.!?।]$/;

  if (punctuationRegex.test(trimmed)) {
    return message; // Already has punctuation, return as-is
  }

  // Add period before any trailing whitespace
  return trimmed + "." + message.slice(trimmed.length);
}

export default function InlineErrorWithContact({
  show,
  onContact,
  messageKey = "account number is not valid",
  ariaLabel = "Bank account contact cs",
}: InlineErrorWithContactProps) {
  const { t } = useTranslation();
  if (!show) return null;

  const errorMessage = t(messageKey);
  const errorMessageWithPunctuation = ensurePunctuation(errorMessage);

  return (
    <p className="text-right text-xs leading-4 text-error-base transition-[max-height]">
      {errorMessageWithPunctuation}{" "}
      <button
        onClick={onContact}
        className="cursor-pointer underline"
        aria-label={ariaLabel}
        data-element-id="button-contact-cs-error"
      >
        {t("contact cs")}
      </button>
    </p>
  );
}
